/**
 * ShieldedField Component
 *
 * Renders a member data field with automatic shielding based on the
 * backend _shielded_fields metadata. When data is shielded (masked by backend),
 * displays the masked value with a lock icon. When revealed (valid reveal token
 * sent to backend), displays the real value with an unlock icon and countdown.
 *
 * Usage:
 *   <ShieldedField
 *     value={member.email}
 *     fieldName="email"
 *     isShielded={member._shielded_fields?.includes('email')}
 *   />
 */

import React from 'react';
import { Lock, Unlock, Eye } from 'lucide-react';
import { useDataShield, useRevealCountdown } from '@/contexts/DataShieldContext';

interface ShieldedFieldProps {
  /** The value to display (may already be masked by backend) */
  value: string | null | undefined;
  /** Field name for identification */
  fieldName: string;
  /** Whether this specific field is shielded (from backend _shielded_fields) */
  isShielded?: boolean;
  /** Show reveal button inline */
  showRevealButton?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Placeholder for empty values */
  emptyPlaceholder?: string;
}

export function ShieldedField({
  value,
  fieldName,
  isShielded = false,
  showRevealButton = true,
  className = '',
  emptyPlaceholder = '-',
}: ShieldedFieldProps) {
  const { isRevealed, requestReveal, isShieldEnabled } = useDataShield();

  // No value at all
  if (value === null || value === undefined || value === '') {
    return <span className={`text-gray-400 ${className}`}>{emptyPlaceholder}</span>;
  }

  // Shield not enabled for this org — show value normally
  if (!isShieldEnabled) {
    return <span className={className}>{value}</span>;
  }

  // Field is shielded (backend returned masked value)
  if (isShielded && !isRevealed) {
    return (
      <span className={`inline-flex items-center gap-1.5 ${className}`}>
        <Lock className="w-3 h-3 text-amber-500 flex-shrink-0" />
        <span className="text-gray-500 dark:text-gray-400 font-mono text-xs tracking-wider">
          {value}
        </span>
        {showRevealButton && (
          <button
            onClick={requestReveal}
            className="ml-1 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
            title="Reveal shielded data"
            type="button"
          >
            <Eye className="w-3.5 h-3.5" />
          </button>
        )}
      </span>
    );
  }

  // Data is revealed (backend returned real value because reveal token was valid)
  if (isRevealed && isShieldEnabled) {
    return (
      <span className={`inline-flex items-center gap-1.5 ${className}`}>
        <Unlock className="w-3 h-3 text-green-500 flex-shrink-0" />
        <span>{value}</span>
      </span>
    );
  }

  // Default: show value normally
  return <span className={className}>{value}</span>;
}

// ============================================================================
// REVEAL STATUS BAR — Floating bar showing reveal countdown
// ============================================================================

export function RevealStatusBar() {
  const { isRevealed, isShieldEnabled, remainingSeconds, revokeReveal } = useDataShield();
  const countdown = useRevealCountdown();

  if (!isShieldEnabled || !isRevealed) {
    return null;
  }

  const isUrgent = remainingSeconds < 60;

  return (
    <div
      className={`fixed bottom-4 right-4 z-50 flex items-center gap-3 px-4 py-2.5 rounded-lg shadow-lg border ${
        isUrgent
          ? 'bg-amber-50 dark:bg-amber-900/30 border-amber-300 dark:border-amber-700'
          : 'bg-green-50 dark:bg-green-900/30 border-green-300 dark:border-green-700'
      }`}
    >
      <Unlock
        className={`w-4 h-4 ${isUrgent ? 'text-amber-600' : 'text-green-600'}`}
      />
      <div className="text-sm">
        <span className="font-medium text-gray-700 dark:text-gray-300">
          Data revealed
        </span>
        <span
          className={`ml-2 font-mono text-xs ${
            isUrgent ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'
          }`}
        >
          {countdown}
        </span>
      </div>
      <button
        onClick={revokeReveal}
        className="ml-2 text-xs px-2 py-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
        type="button"
      >
        Lock
      </button>
    </div>
  );
}

// ============================================================================
// SHIELD INDICATOR — Shows shield status on member row in list view
// ============================================================================

export function ShieldIndicator({ isShielded }: { isShielded: boolean }) {
  if (!isShielded) return null;

  return (
    <span title="Data is shielded by privacy policy">
      <Lock className="w-3.5 h-3.5 text-amber-500" />
    </span>
  );
}
